import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {Link} from 'react-router-dom';
import {Button, DatePicker, Popover, Table} from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import * as moment from 'moment';
import * as qs from 'query-string';
import {
  ClientRevenueEntry,
  ClientRevenueSummaryGridResponse,
  ClientRevenueTotals,
  ErrorResponse
} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import * as paths from '../paths';
import locales from '../locales';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import ClientDetailsAlert from '../client-details/ClientDetailsAlert';
import {ErrorAlert} from '../common/ErrorAlert';
import * as formUtils from '../utils/form-utils';
import {formItemLayout} from '../utils/form-utils';
import {formatDate, MOMENT_YEAR_MONTH_FORMAT} from '../utils/date-utils';
import {simpleColumn} from '../utils/table-utils';
import InvoiceCell from '../invoicing/InvoiceCell';
import {renderFixedPriceColumn} from '../utils/number-utils';
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import './ClientRevenueSummaryPage.css';

const {MonthPicker} = DatePicker;

const monthPickerDisabledDateFn = (current: moment.Moment | null): boolean => {
  return current !== null && current.isAfter(moment(), 'month');
};

const sumRevenueEntries = (entries: ClientRevenueEntry[]) => entries.reduce((sums, entry) => ({
  total: sums.total + entry.total,
  invoicedTotal: sums.invoicedTotal + entry.invoicedTotal,
  outstandingTotal: sums.outstandingTotal + entry.outstandingTotal,
}), {total: 0, invoicedTotal: 0, outstandingTotal: 0});


interface State {
  selectedYear: number;
  selectedMonth: number;
  data?: ClientRevenueSummaryGridResponse;
  error?: ErrorResponse;
}

class ClientRevenueSummaryPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;
  private requestId = 0;

  constructor(props: RouteComponentProps<paths.IdPageUrlParams>) {
    super(props);
    this.state = this.readPeriod();
  }

  public componentDidMount() {
    this.reload();
  }

  public componentDidUpdate(previous: RouteComponentProps<paths.IdPageUrlParams>) {
    if (previous.match.params.id !== this.props.match.params.id || previous.location.search !== this.props.location.search) {
      this.setState({...this.readPeriod(), data: undefined}, this.reload);
    }
  }

  public componentWillUnmount() {
    this.requestId++;
  }

  public render() {
    const {selectedYear, selectedMonth, data, error} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        <FormItem {...formUtils.formItemLayout} label={locales.month_capitalized}>
          <MonthPicker
            format={MOMENT_YEAR_MONTH_FORMAT}
            value={moment().date(1).year(selectedYear).month(selectedMonth)}
            disabledDate={monthPickerDisabledDateFn}
            onChange={this.onMonthChange}
            placeholder={locales.choose}
            allowClear={false}
          />
        </FormItem>
        {data && <>
            <FormItem {...formItemLayout} label={locales.client}>
                <ClientDetailsAlert closable={false} client={data.client}/>
            </FormItem>
          {this.renderTable(locales.maintenance_worksheets, data.maintenances, paths.viewMaintenanceHistoryItemPath)}
          {this.renderTable(locales.sales, data.sales, paths.viewSalePath)}
          {this.renderTable(locales.manual, data.manualInvoices)}
          {this.renderRevenueTotals()}
        </>}
        {error && <ErrorAlert style={{marginTop: 16}} error={error}/>}
      </>
    );
  }

  private renderTotalSummaryLabel = (title: string, totals: ClientRevenueTotals) => {
    return (
      <span>
        {title}<RevenueTotalsInfo title={title} totals={totals}/>
      </span>
    );
  }

  private renderRevenueTotals = () => {
    const {data} = this.state;

    if (!data) {
      return null;
    }

    return <>
      <SectionHeaderFormItem title={locales.revenue_totals} style={{marginTop: 24, marginBottom: 8}}/>
      <FormItem {...formItemLayout} style={{marginBottom: 0}}
                label={this.renderTotalSummaryLabel(locales.total, data.total)}>
        <b>{renderFixedPriceColumn(data.total.total)}</b>
      </FormItem>
      <FormItem {...formItemLayout}
                style={{marginBottom: 0}}
                label={
                  this.renderTotalSummaryLabel(locales.invoiced_total, data.invoiced)
                }
      >
        <b>{renderFixedPriceColumn(data.invoiced.total)}</b>
      </FormItem>
      <FormItem {...formItemLayout}
                style={{marginBottom: 0}}
                label={
                  this.renderTotalSummaryLabel(locales.finalized, data.finalized)
                }
      >
        <b>{renderFixedPriceColumn(data.finalized.total)}</b>
      </FormItem>
      <FormItem {...formItemLayout} style={{marginBottom: 0}} label={
        this.renderTotalSummaryLabel(locales.outstanding_total, data.outstanding)
      }>
        <b>{renderFixedPriceColumn(data.outstanding.total)}</b>
      </FormItem>
    </>
  }

  private renderTable = (title: string, entries: ClientRevenueEntry[], sourcePath?: (id: number) => string) => (
    <>
      <SectionHeaderFormItem title={title} style={{marginTop: 24, marginBottom: 8}}/>
      <Table<ClientRevenueEntry>
        dataSource={entries}
        columns={[
          simpleColumn(locales.name, entry => sourcePath
            ? <Link to={sourcePath(entry.entityId)}>{entry.name}</Link>
            : entry.name),
          simpleColumn(locales.date, entry => formatDate(entry.date)),
          {
            ...simpleColumn<ClientRevenueEntry>(locales.total, entry => renderFixedPriceColumn(entry.total)),
            className: 'align-right',
          },
          {
            ...simpleColumn<ClientRevenueEntry>(locales.invoiced_total, entry => renderFixedPriceColumn(entry.invoicedTotal)),
            className: 'align-right',
          },
          simpleColumn(locales.invoices, entry => entry.finalized && entry.invoices.length === 0
            ? locales.finalized
            : entry.invoices.length
              ? entry.invoices.map(invoice => <InvoiceCell key={invoice.id} finalized={false} invoice={invoice}/>)
              : '-'),
        ]}
        rowKey="id"
        bordered={true}
        size="small"
        pagination={false}
        scroll={{x: 576}}
      />
      {entries.length > 0 && (
        <FooterTotalCell count={entries.length} {...sumRevenueEntries(entries)}/>
      )}
    </>
  );

  private readPeriod = () => {
    const {year, month} = qs.parse(this.props.location.search);
    return {
      selectedYear: year ? Number(year) : moment().year(),
      selectedMonth: month ? Number(month) : moment().month(),
    };
  };

  private onMonthChange = (date: moment.Moment) => {
    this.props.history.replace(paths.clientRevenueSummaryPath(Number(this.props.match.params.id), date.year(), date.month()));
  };

  private reload = () => {
    const requestId = ++this.requestId;
    const promise = furoApi.summaries.getClientRevenueSummaries({
      clientId: Number(this.props.match.params.id),
      year: this.state.selectedYear,
      month: this.state.selectedMonth,
    }).then((data) => {
      if (requestId === this.requestId) {
        this.setState({data, error: undefined});
        setAppHeaderSubtitle(this.props, data.client.name);
      }
    }).catch((error: ErrorResponse) => {
      if (requestId === this.requestId) {
        this.setState({error, data: undefined});
      }
    });
    withLoadingOverlay(promise, this.loadingHandler);
  };
}

const RevenueTotalsInfo = ({title, totals}: { title: string; totals: ClientRevenueTotals }) => (
  <Popover
    title={title}
    trigger="click"
    content={
      <table>
        <tbody>
        {[
          {label: locales.maintenance_worksheets, amount: totals.maintenances},
          {label: locales.sales, amount: totals.sales},
          {label: locales.manual, amount: totals.manualInvoices},
          {label: locales.total, amount: totals.total},
        ].map((row, index) => (
          <tr key={row.label} style={{fontWeight: index === 3 ? 'bold' : 'normal'}}>
            <th scope="row" style={{textAlign: 'left', fontWeight: 'inherit', paddingRight: 24}}>{row.label}</th>
            <td style={{textAlign: 'right', whiteSpace: 'nowrap'}}>{renderFixedPriceColumn(row.amount)}</td>
          </tr>
        ))}
        </tbody>
      </table>
    }
  >
    <Button type="link" size="small" icon="info-circle" aria-label={title} style={{marginLeft: 8}}/>
  </Popover>
);

interface RevenueTotalProps {
  count: number;
  total: number;
  invoicedTotal: number;
  outstandingTotal: number;
}

const FooterTotalCell = ({count, total, invoicedTotal, outstandingTotal}: RevenueTotalProps) => {
  return (
    <div className="client-revenue-totals">
      <table>
        <tbody>
        <tr>
          <td>{locales.outstanding_total}</td>
          <td><b>{renderFixedPriceColumn(outstandingTotal)}</b></td>
        </tr>
        <tr>
          <td>{locales.invoiced_total}</td>
          <td><b>{renderFixedPriceColumn(invoicedTotal)}</b></td>
        </tr>
        <tr className="summary-separator">
          <td>{locales.total} <b>{count}</b> {locales.entry}</td>
          <td><b>{renderFixedPriceColumn(total)}</b></td>
        </tr>
        </tbody>
      </table>
    </div>
  );
};

export default withRouter(ClientRevenueSummaryPage);



// WEBPACK FOOTER //
// ./src/summary/ClientRevenueSummaryPage.tsx